import { useState } from 'react';
import { useConnectionContext } from './ConnectionContext';
import { useSmartMatchHints, SmartMatchHint } from './useSmartMatchHints';
import { SmartMatchFieldHint } from './SmartMatchFieldHint';
import { getConnectionValue, getInputValue, isConnectionApplied } from './utils';
import { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { ReplaceAnswerModal } from './ReplaceAnswerModal';

interface Props {
  valueListCode?: string;
  utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation' | 'numberScale' | 'unit'>;
}

export const SmartMatchFieldHintContainer = ({ valueListCode, utr }: Props) => {
  const { utrvId, setConnection, setInputData, currentInputData } = useConnectionContext();
  const hints = useSmartMatchHints(utrvId);
  const [pendingHint, setPendingHint] = useState<SmartMatchHint | null>(null);
  const [appliedConnectionId, setAppliedConnectionId] = useState<string | null>(null);
  const showWarning = pendingHint !== null;

  const matchingHints = hints.filter((hint) => {
    if (valueListCode !== undefined) {
      return hint.valueListCode === valueListCode;
    }
    return !hint.valueListCode;
  });

  if (matchingHints.length === 0) {
    return null;
  }

  const applyHint = (hint: SmartMatchHint) => {
    const utrv = {
      _id: utrvId,
      numberScale: currentInputData.numberScale,
      valueData: currentInputData.valueData,
      status: '',
    };

    const convertedValue = getConnectionValue({
      connection: hint.connection,
      utr,
      utrv,
      currentInputData,
    });

    setConnection(hint.connection);
    setInputData(convertedValue);
    setAppliedConnectionId(hint.connection._id);
  };

  const handleUseValue = (hint: SmartMatchHint) => {
    const currentValue = getInputValue({ utr, currentInputData, valueListCode: hint.connection.valueListCode });

    if (currentValue !== undefined) {
      setPendingHint(hint);
      return;
    }

    applyHint(hint);
  };

  const handleConfirm = () => {
    if (pendingHint) {
      applyHint(pendingHint);
    }
    setPendingHint(null);
  };

  const handleCancel = () => {
    setPendingHint(null);
  };

  return (
    <>
      {matchingHints.map((hint) => (
        <SmartMatchFieldHint
          key={hint.connection._id}
          hint={hint}
          onUseValue={() => handleUseValue(hint)}
          isApplied={appliedConnectionId === hint.connection._id || isConnectionApplied(hint.connection, currentInputData.valueData)}
        />
      ))}
      <ReplaceAnswerModal open={showWarning} toggle={handleCancel} handleConfirm={handleConfirm} />
    </>
  );
};
